import { Button, LoadSpinner } from '@calizahq/react-components-v2'
import { Row } from '@tanstack/react-table'
import React, { useCallback, useMemo, useState } from 'react'

import { BottomSheet, FAB, NoResults, Table } from '@/components'
import { useUserContext } from '@/context'
import { SortBy, useTicketFiltersContext } from '@/context/filters/ticketFilters/ticketFilters'
import { CreateTicketForm } from '@/forms'
import { useMobile, useToggle } from '@/hooks'
import useTickets from '@/hooks/useTickets/useTickets'
import { TicketListRow, Type } from '@/types/Ticket'

import { AssignFilter } from './components/AssignFilter'
import { MobileFilter } from './MobileFilter'
import { MobileTicketCard } from './MobileTicketCard'
import { buildTicketListColumns } from './Tickets.data'
import { TicketsFilterBar } from './TicketsFilterBar'

export const Tickets = () => {
  const { isTablet } = useMobile()
  const { sortBy, setSortBy } = useTicketFiltersContext()
  const { data: ticketsData, refetch, isLoading } = useTickets()
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const { isShown, toggle } = useToggle()
  const { user } = useUserContext()
  const isSuperAdmin = !!user?.isSuperAdmin

  const [rowSelected, setRowSelection] = useState<Row<any>[]>([])
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const data = useMemo(() => {
    const allTickets = ticketsData?.tickets ?? []
    // Non-admins never see Conversation tickets; they appear as a flat list
    const tickets = isSuperAdmin ? allTickets : allTickets.filter((t) => t.ticketType !== Type.CONVERSATION)

    const resultIds = new Set(tickets.map((t) => t.id))

    // Any ticket whose parent appears in results gets removed from the top level
    const absorbedIds = new Set<number>(
      tickets.filter((t) => t.parentTicketId && resultIds.has(t.parentTicketId)).map((t) => t.id),
    )

    const flattened: TicketListRow[] = []
    for (const ticket of tickets) {
      if (absorbedIds.has(ticket.id)) continue

      flattened.push(ticket)

      if (ticket.allowAsParent && ticket.childTickets?.length && expandedIds.has(ticket.id)) {
        for (const child of ticket.childTickets) {
          flattened.push({ ...child, _isChildRow: true } as TicketListRow)
        }
      }
    }
    return flattened
  }, [ticketsData, expandedIds, isSuperAdmin])

  const columns = useMemo(
    () => buildTicketListColumns(expandedIds, handleToggleExpand),
    [expandedIds, handleToggleExpand],
  )

  const setSortBySelection = (selectedSortBy: SortBy[]) => {
    if (selectedSortBy && selectedSortBy.length) {
      setSortBy(selectedSortBy[0])
    } else {
      sortBy && setSortBy(null)
    }
  }

  // Right now the table is attempting to render 200+ items at once, which causes a UI bug where an empty table shows for some time.
  // react-table has to do some javascript stuff to prepare data for rendering.
  // In the future we will implement cursor based pagination to preserve the infinite scroll feel and fix the performance issues with the large data set.
  return (
    <div className="flex w-full flex-col p-4 md:p-8">
      <div className="mb-2 flex w-full items-center justify-between md:mb-8 md:flex-row">
        <h2 className=" text-header-lg leading-120">Tickets</h2>

        <div className="hidden md:flex">
          <AssignFilter rowSelected={rowSelected} />
          <Button className="bg-action-primary" onClick={modalToggle} size="small">
            Create Ticket
          </Button>
        </div>
        <MobileFilter />
      </div>
      <TicketsFilterBar />
      {isLoading ? (
        <div className="flex h-20 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {isTablet ? (
            <>
              {(data.length > 0 &&
                data.map((ticket, index) => (
                  <MobileTicketCard
                    expandedIds={expandedIds}
                    includeMarket={true}
                    key={index}
                    onToggleExpand={handleToggleExpand}
                    ticket={ticket}
                  />
                ))) || <NoResults />}
              <FAB isShown={isShown} toggle={toggle}>
                <button
                  className="option w-full whitespace-nowrap py-3 text-left font-medium"
                  onClick={() => modalToggle()}
                >
                  Create Ticket
                </button>
              </FAB>
            </>
          ) : (
            <Table
              columns={columns}
              data={data}
              disableSortBy={false}
              manualSortBy={true}
              rowSelection={true}
              setRowSelection={setRowSelection}
              setSortBySelection={setSortBySelection}
            />
          )}
        </>
      )}
      <BottomSheet isVisible={modalShown} mobileFullHeight={true} onClose={modalToggle}>
        <CreateTicketForm refetchOnSuccess={refetch} toggle={modalToggle} />
      </BottomSheet>
    </div>
  )
}
